/**
 * The "Ask" catalog: reviewed answers to "where do I do X?" questions, about
 * any page. No model composes an answer — a bad answer is a content bug in an
 * entry here. Pure data; registry.test.ts checks every route and anchor.
 *
 * Change a page or a rule an entry quotes → re-read the entries whose `source`
 * is that page's guide, in the same commit.
 */
import { OFFER_STATUSES, PAID_STATUSES, UNPAID_INVOICE_STATUSES, type AskOffer } from "./journey";
import type { HelpPermission, HelpTranslation } from "../types";

export const ASK_AREAS = ["offers", "invoices", "payments", "export", "settings"] as const;
export type AskArea = (typeof ASK_AREAS)[number];

export const ASK_AREA_LABEL: Record<AskArea, { en: string; lv: string }> = {
  offers: { en: "Offers", lv: "Piedāvājumi" },
  invoices: { en: "Invoices", lv: "Rēķini" },
  payments: { en: "Payments & policies", lv: "Maksājumi un polises" },
  export: { en: "Export & import", lv: "Eksports un imports" },
  settings: { en: "Settings", lv: "Iestatījumi" },
};

export interface AskEntryText {
  title?: string;
  body?: string;
  needs?: string;
}

export interface AskEntry {
  /** Stable id shown on the card, e.g. OFF-001. */
  id: string;
  area: AskArea;
  /** The job, as the user would say it. */
  title: string;
  /** 2–6 sentences: where it is, what to fill in, the rule nobody can guess. */
  body: string;
  /** What has to exist first. */
  needs?: string;
  /** Where "Take me there" navigates (a path, query allowed). */
  route: string;
  /** The control to outline (or press, with `open`) once there. */
  anchor?: string;
  /** Press the anchor when it is the only one on screen ("Take me there and open it"). */
  open?: boolean;
  /** The answer is about one existing offer: which one to bring on screen, and how (see journey.ts). */
  offer?: AskOffer;
  /** Words people use that the text does not contain. */
  keywords?: string[];
  /** Hide from viewers without this permission (only when the UI hides it too). */
  requires?: HelpPermission;
  /** The guide this entry was distilled from (a key of HELP_REGISTRY). */
  source: string;
  i18n?: Partial<Record<HelpTranslation, AskEntryText>>;
}

export const ASK_CATALOG: AskEntry[] = [
  // --- Offers --------------------------------------------------------------------
  {
    id: "OFF-001",
    area: "offers",
    title: "Create an MTPL offer",
    body: 'Click "New Offer" → "New MTPL Offer". On General Information enter the plate, VIN, certificate number or owner code and press "Prefill"; check the vehicle and client data. On the Offer tab choose the start date, the insurers and the periods (1–12 months), then "Create Offer" — the offer gets its number and its PDF and Word documents.',
    route: "/offer?type=mtpl",
    anchor: "offer-general-prefill-button",
    keywords: ["octa", "new", "quote", "compulsory", "liability", "start"],
    source: "/offer",
    i18n: { lv: { title: "Izveidot OCTA piedāvājumu", body: "Noklikšķiniet uz “Jauns piedāvājums” → “Jauns OCTA piedāvājums”. Cilnē “Vispārīgā informācija” ievadiet numura zīmi, VIN, apliecības numuru vai īpašnieka kodu un nospiediet “Aizpildīt”; pārbaudiet transportlīdzekļa un klienta datus. Cilnē “Piedāvājums” izvēlieties sākuma datumu, apdrošinātājus un periodus (1–12 mēneši), pēc tam “Izveidot piedāvājumu” — piedāvājums saņem numuru un savus PDF un Word dokumentus." } },
  },
  {
    id: "OFF-002",
    area: "offers",
    title: "Create a CASCO offer",
    body: 'Click "New Offer" → "New CASCO Offer". Besides the vehicle and client, CASCO needs the vehicle value, the other driver\'s age and experience and the coverage territory. On the Offer tab choose the insurers and payment plans (1, 2, 4 or 12 payments) and press "Create Offer".',
    route: "/offer?type=casco",
    anchor: "offer-general-prefill-button",
    keywords: ["kasko", "new", "quote", "own damage", "start"],
    source: "/offer",
    i18n: { lv: { title: "Izveidot KASKO piedāvājumu", body: "Noklikšķiniet uz “Jauns piedāvājums” → “Jauns KASKO piedāvājums”. Papildus transportlīdzeklim un klientam KASKO vajag transportlīdzekļa vērtību, cita vadītāja vecumu un pieredzi un darbības teritoriju. Cilnē “Piedāvājums” izvēlieties apdrošinātājus un maksājumu plānus (1, 2, 4 vai 12 maksājumi) un nospiediet “Izveidot piedāvājumu”." } },
  },
  {
    id: "OFF-003",
    area: "offers",
    title: "Find an offer",
    body: 'Type a client name, client code or licence plate in the search box of the offers list; narrow it with All / MTPL / Casco and with the filters in "Column options". Click the Offer ID to open the offer — clicking the row only selects it.',
    route: "/",
    anchor: "offers-search",
    keywords: ["search", "look up", "open", "list", "client", "plate"],
    source: "/",
    i18n: { lv: { title: "Atrast piedāvājumu", body: "Piedāvājumu saraksta meklēšanas laukā ierakstiet klienta nosaukumu, klienta kodu vai numura zīmi; sašauriniet rezultātu ar “Visi / OCTA / KASKO” un filtriem sadaļā “Kolonnas opcijas”. Noklikšķiniet uz piedāvājuma ID, lai to atvērtu, — klikšķis uz rindas to tikai atlasa." } },
  },
  {
    id: "OFF-004",
    area: "offers",
    title: "Fill the vehicle data automatically (Prefill)",
    body: 'On a new offer\'s General Information tab, enter at least one of licence plate, VIN, registration certificate number or owner code and press "Prefill". The vehicle data is filled from the register and, when the owner is found in GOS, the client too. Prefill is not available once the offer is created.',
    route: "/offer?type=mtpl",
    anchor: "offer-general-prefill-button",
    keywords: ["prefill", "register", "lookup", "vin", "plate", "automatic"],
    source: "/offer",
    i18n: { lv: { title: "Automātiski aizpildīt transportlīdzekļa datus (Aizpildīt)", body: "Jauna piedāvājuma cilnē “Vispārīgā informācija” ievadiet vismaz vienu no šiem: numura zīme, VIN, reģistrācijas apliecības numurs vai īpašnieka kods, un nospiediet “Aizpildīt”. Transportlīdzekļa dati tiek aizpildīti no reģistra, un, ja īpašnieks atrasts GOS, arī klienta dati. Kad piedāvājums ir izveidots, “Aizpildīt” vairs nav pieejams." } },
  },
  {
    id: "OFF-005",
    area: "offers",
    title: "Renew or copy an existing offer",
    body: 'In the offers list open ⋮ next to the offer and choose "Duplicate Offer". A new offer opens with the same vehicle, client and insurance data but no number, status or quotes; change what differs and create it. Use this whenever you need to quote again with changed data, because a created offer is locked.',
    route: "/",
    anchor: "offers-row-menu-duplicate",
    offer: { action: "menu" },
    keywords: ["duplicate", "copy", "renewal", "renew", "again", "clone"],
    source: "/",
    i18n: { lv: { title: "Atjaunot vai kopēt esošu piedāvājumu", body: "Piedāvājumu sarakstā atveriet ⋮ blakus piedāvājumam un izvēlieties “Dublēt piedāvājumu”. Atveras jauns piedāvājums ar tiem pašiem transportlīdzekļa, klienta un apdrošināšanas datiem, bet bez numura, statusa un kotācijām; nomainiet atšķirīgo un izveidojiet to. Izmantojiet šo vienmēr, kad vajag jaunu kotāciju ar mainītiem datiem, jo izveidots piedāvājums ir bloķēts." } },
  },
  {
    id: "OFF-006",
    area: "offers",
    title: "Send an offer to the client by e-mail",
    body: 'Open the offer and, on the Offer tab, click the envelope next to the offer PDF. The e-mail is pre-filled with the client\'s address, a subject and a message from the template, with the documents attached; adjust it and press "Send Email". The status becomes Offer Sent.',
    needs: "The offer must be created; once it has an invoice, send the invoice instead.",
    route: "/offer/:offerId",
    anchor: "offer-send-offer-email",
    open: true,
    offer: { statuses: OFFER_STATUSES, action: "open" },
    keywords: ["email", "mail", "send", "client", "pdf"],
    source: "/offer",
    i18n: { lv: { title: "Nosūtīt piedāvājumu klientam pa e-pastu", body: "Atveriet piedāvājumu un cilnē “Piedāvājums” noklikšķiniet uz aploksnes blakus piedāvājuma PDF. E-pasts ir aizpildīts ar klienta adresi, tematu un ziņojumu no veidnes, un dokumenti ir pievienoti; pielāgojiet to un nospiediet “Nosūtīt E-pastu”. Statuss kļūst “Piedāvājums nosūtīts”.", needs: "Piedāvājumam jābūt izveidotam; ja tam jau ir rēķins, nosūtiet rēķinu." } },
  },
  {
    id: "OFF-007",
    area: "offers",
    title: "Change a premium or leave an insurer out",
    body: 'On a created offer\'s Offer tab, the pencil on a premium (or a CASCO deductible) lets you override it, and the checkbox in an insurer\'s column header excludes it. The changes are kept only after "Update Offer", which also regenerates the documents.',
    needs: "The offer must be created and must not have an invoice yet.",
    route: "/offer/:offerId",
    anchor: "offer-quotes-table",
    offer: { statuses: OFFER_STATUSES, action: "open" },
    keywords: ["edit", "override", "price", "exclude", "deductible", "update"],
    source: "/offer",
    i18n: { lv: { title: "Mainīt prēmiju vai izslēgt apdrošinātāju", body: "Izveidota piedāvājuma cilnē “Piedāvājums” zīmulis pie prēmijas (vai KASKO pašriska) ļauj to aizstāt, un izvēles rūtiņa apdrošinātāja kolonnas virsrakstā to izslēdz. Izmaiņas tiek saglabātas tikai pēc “Atjaunināt piedāvājumu”, kas arī no jauna izveido dokumentus.", needs: "Piedāvājumam jābūt izveidotam, un tam vēl nedrīkst būt rēķina." } },
  },
  {
    id: "OFF-008",
    area: "offers",
    title: "Cancel (invalidate) an offer",
    body: 'In the offers list open ⋮ next to the offer and choose "Invalidate Offer", then confirm. Only possible while no insurer has been chosen and any invoice is neither sent nor paid; the offer disappears from the list and cannot be restored.',
    route: "/",
    anchor: "offers-row-menu-invalidate",
    offer: { statuses: OFFER_STATUSES, action: "menu" },
    keywords: ["cancel", "delete", "remove", "invalidate", "void"],
    source: "/",
    i18n: { lv: { title: "Atcelt (anulēt) piedāvājumu", body: "Piedāvājumu sarakstā atveriet ⋮ blakus piedāvājumam, izvēlieties “Atcelt piedāvājumu” un apstipriniet. Iespējams tikai tad, ja nav izvēlēts apdrošinātājs un rēķins nav nedz nosūtīts, nedz apmaksāts; piedāvājums pazūd no saraksta, un to nevar atjaunot." } },
  },

  // --- Invoices ------------------------------------------------------------------
  {
    id: "INV-001",
    area: "invoices",
    title: "Create an invoice for the quote the client chose",
    body: 'Open the offer, select the chosen premium in the quotes table and press "Create Invoice". Fill in the invoice number, due date, document number, premium and discount, and the payer ("Same as client" or a different payer from GOS). The offer moves to status Invoice and its quotes are locked.',
    needs: "A created offer with no pending quote changes.",
    route: "/offer/:offerId",
    anchor: "offer-create-invoice-button",
    offer: { statuses: OFFER_STATUSES, action: "open" },
    keywords: ["bill", "invoice", "chosen", "accept", "payer"],
    source: "/offer",
    i18n: { lv: { title: "Izveidot rēķinu klienta izvēlētajai kotācijai", body: "Atveriet piedāvājumu, kotāciju tabulā atlasiet izvēlēto prēmiju un nospiediet “Izveidot rēķinu”. Aizpildiet rēķina numuru, apmaksas termiņu, dokumenta numuru, prēmiju un atlaidi un maksātāju (“Tāds pats kā klients” vai cits maksātājs no GOS). Piedāvājums pāriet statusā “Rēķins”, un tā kotācijas tiek bloķētas.", needs: "Izveidots piedāvājums bez nesaglabātām kotāciju izmaiņām." } },
  },
  {
    id: "INV-002",
    area: "invoices",
    title: "Combine several invoices into one group invoice",
    body: 'In the offers list tick two or more offers in status Invoice, then "Create group invoice" in the bar at the bottom and "Continue". Choose the payer, check the suggested number and due date, and create it; every included offer moves to Group Invoice.',
    needs: "At least two offers in status Invoice, each with an invoice number.",
    route: "/",
    anchor: "offers-create-group-invoice",
    offer: { statuses: ["OfferStatus.INVOICE"], action: "select", count: 2 },
    keywords: ["group", "combine", "merge", "one invoice", "several", "fleet"],
    source: "/",
    i18n: { lv: { title: "Apvienot vairākus rēķinus vienā grupas rēķinā", body: "Piedāvājumu sarakstā atzīmējiet divus vai vairāk piedāvājumus statusā “Rēķins”, pēc tam joslā apakšā nospiediet “Izveidot grupas rēķinu” un “Turpināt”. Izvēlieties maksātāju, pārbaudiet ieteikto numuru un apmaksas termiņu un izveidojiet to; katrs iekļautais piedāvājums pāriet statusā “Grupas rēķins”.", needs: "Vismaz divi piedāvājumi statusā “Rēķins”, katrs ar rēķina numuru." } },
  },
  {
    id: "INV-003",
    area: "invoices",
    title: "Send an invoice by e-mail",
    body: "Open the offer; on the Offer tab, click the envelope next to the invoice PDF (or next to the group invoice PDF). The e-mail goes to the payer's address by default.",
    needs: "The invoice must exist.",
    route: "/offer/:offerId",
    anchor: "offer-documents",
    offer: { statuses: [...UNPAID_INVOICE_STATUSES, ...PAID_STATUSES], action: "open" },
    keywords: ["email", "send", "invoice", "payer"],
    source: "/offer",
    i18n: { lv: { title: "Nosūtīt rēķinu pa e-pastu", body: "Atveriet piedāvājumu; cilnē “Piedāvājums” noklikšķiniet uz aploksnes blakus rēķina PDF (vai blakus grupas rēķina PDF). Pēc noklusējuma e-pasts tiek sūtīts uz maksātāja adresi.", needs: "Rēķinam jābūt izveidotam." } },
  },

  // --- Payments & policies ----------------------------------------------------------
  {
    id: "PAY-001",
    area: "payments",
    title: "Record that an invoice was paid",
    body: 'For one offer: ⋮ → "Mark Invoice as Paid" in the offers list, then confirm — the status becomes Invoice Paid (for an offer in a group invoice only that offer; "Mark Group Invoice as Paid" settles all its unpaid offers). For several at once, tick them and use "Check payments", which matches bank statement entries to the invoices and marks the ticked ones as paid.',
    needs: "The offer must be in status Invoice, Invoice Sent or a Group Invoice status.",
    route: "/",
    anchor: "offers-row-menu-mark-paid",
    offer: { statuses: UNPAID_INVOICE_STATUSES, action: "menu" },
    keywords: ["paid", "payment", "bank", "received", "settle", "statement"],
    source: "/",
    i18n: { lv: { title: "Reģistrēt, ka rēķins ir apmaksāts", body: "Vienam piedāvājumam: piedāvājumu sarakstā ⋮ → “Atzīmēt rēķinu kā apmaksātu”, pēc tam apstipriniet — statuss kļūst “Rēķins apmaksāts” (grupas rēķinā iekļautam piedāvājumam tikai šim piedāvājumam; “Atzīmēt grupas rēķinu kā apmaksātu” nokārto visus tā neapmaksātos piedāvājumus). Vairākiem uzreiz: atzīmējiet tos un izmantojiet “Pārbaudīt maksājumus”, kas sasaista bankas izraksta ierakstus ar rēķiniem un atzīmētos atzīmē kā apmaksātus.", needs: "Piedāvājumam jābūt statusā “Rēķins”, “Rēķins nosūtīts” vai kādā no grupas rēķina statusiem." } },
  },
  {
    id: "PAY-002",
    area: "payments",
    title: "Confirm the insurer issued the policy",
    body: 'Tick the paid offers in the list and press "Check policies": the policies found in POL are compared with the offer (client, insurer, premium, validity) and full matches are pre-ticked; confirm to mark them Policy Issued. For a single offer use ⋮ → "Mark Paid Invoice as Issued".',
    needs: "The invoice must be paid and the offer must have a VIN.",
    route: "/",
    anchor: "offers-check-policies",
    offer: { statuses: PAID_STATUSES, action: "select" },
    keywords: ["policy", "issued", "pol", "number", "confirm"],
    source: "/",
    i18n: { lv: { title: "Apstiprināt, ka apdrošinātājs izsniedza polisi", body: "Sarakstā atzīmējiet apmaksātos piedāvājumus un nospiediet “Pārbaudīt polises”: POL atrastās polises tiek salīdzinātas ar piedāvājumu (klients, apdrošinātājs, prēmija, derīgums), un pilnīgas atbilstības ir atzīmētas jau iepriekš; apstipriniet, lai tās atzīmētu kā “Polise izsniegta”. Vienam piedāvājumam izmantojiet ⋮ → “Atzīmēt apmaksāto rēķinu kā izsniegtu”.", needs: "Rēķinam jābūt apmaksātam, un piedāvājumam jābūt VIN." } },
  },

  // --- Export & import ----------------------------------------------------------------
  {
    id: "EXP-001",
    area: "export",
    title: "Export offers to Excel or PDF",
    body: 'In the offers list press "Export Excel" or "Export PDF" and choose the period (by request date; the last two months are suggested). Every offer in the period that matches the current search, filters and sort is exported with the visible columns — not only the rows on screen.',
    route: "/",
    anchor: "offers-export-excel",
    open: true,
    keywords: ["excel", "pdf", "download", "report", "xlsx"],
    source: "/",
    i18n: { lv: { title: "Eksportēt piedāvājumus uz Excel vai PDF", body: "Piedāvājumu sarakstā nospiediet “Eksportēt Excel” vai “Eksportēt PDF” un izvēlieties periodu (pēc pieprasījuma datuma; tiek ieteikti pēdējie divi mēneši). Tiek eksportēts katrs perioda piedāvājums, kas atbilst pašreizējai meklēšanai, filtriem un kārtošanai, ar redzamajām kolonnām — ne tikai ekrānā redzamās rindas." } },
  },
  {
    id: "EXP-002",
    area: "export",
    title: "Prefill many vehicles from an Excel file",
    body: 'In the offers list press "Bulk Prefill", drop one .xlsx or .xls file with the vehicles and, if wanted, tick "Create MTPL offers for vehicles". Large files can take a while — you may close the dialog; the result file is e-mailed to you.',
    route: "/",
    anchor: "offers-bulk-prefill",
    open: true,
    keywords: ["bulk", "import", "upload", "many", "fleet", "excel"],
    source: "/",
    i18n: { lv: { title: "Aizpildīt daudzus transportlīdzekļus no Excel faila", body: "Piedāvājumu sarakstā nospiediet “Masveida aizpildīšana”, ievelciet vienu .xlsx vai .xls failu ar transportlīdzekļiem un, ja vēlaties, atzīmējiet “Izveidot OCTA piedāvājumus transportlīdzekļiem”. Lieli faili var aizņemt laiku — dialoglodziņu var aizvērt; rezultāta fails tiks nosūtīts jums pa e-pastu." } },
  },

  // --- Settings -----------------------------------------------------------------------
  {
    id: "SET-001",
    area: "settings",
    title: "Choose which columns the offers list shows",
    body: 'Press "Column options" in the offers list: add columns from the grouped, searchable list, reorder, filter or remove them in the "Selected" list (only selected columns filter) and save the layout as a named view, optionally as your default. Changes apply only when you press "Apply"; "Close" discards them, and "Save current view" keeps them in the selected view.',
    route: "/",
    anchor: "offers-column-options",
    open: true,
    keywords: ["columns", "view", "filter", "layout", "status filter", "group", "search fields", "unsaved changes"],
    source: "/",
    i18n: { lv: { title: "Izvēlēties, kuras kolonnas rāda piedāvājumu saraksts", body: "Piedāvājumu sarakstā nospiediet “Kolonnas opcijas”: pievienojiet kolonnas no grupēta saraksta ar meklēšanu, sarakstā “Atlasītās” tās pārkārtojiet, filtrējiet vai noņemiet (filtrēt var tikai pēc atlasītajām kolonnām) un saglabājiet izkārtojumu kā nosauktu skatu, pēc izvēles kā noklusējuma skatu. Izmaiņas tiek piemērotas tikai tad, kad nospiežat “Lietot”; “Aizvērt” tās atmet, bet “Saglabāt pašreizējo skatu” tās saglabā izvēlētajā skatā." } },
  },
  {
    id: "SET-002",
    area: "settings",
    title: "Look up an insurer's CASCO conditions",
    body: 'Press "CASCO Conditions" in the offers list toolbar to open each insurer\'s CASCO conditions used in quotes and documents. Administrators can change them; read-only administrators can only view them.',
    route: "/",
    anchor: "offers-casco-conditions",
    requires: "canViewSettings",
    keywords: ["conditions", "terms", "insurer", "settings", "casco"],
    source: "/",
    i18n: { lv: { title: "Apskatīt apdrošinātāja KASKO nosacījumus", body: "Piedāvājumu saraksta rīkjoslā nospiediet “KASKO nosacījumi”, lai atvērtu katra apdrošinātāja KASKO nosacījumus, ko izmanto kotācijās un dokumentos. Administratori tos var mainīt; tikai lasīšanas administratori tos var tikai skatīt." } },
  },
];
